/**
 * Central voice language resolution (V4-LANGUAGE-STORAGE).
 * One source of truth for recognition, transcription, TTS, and text direction.
 */

import { SUPPORTED_VOICE_LANGUAGES } from '../pages/settings/voiceConstants';
import type { ResolvedVoiceLanguage } from './types';

export const DEFAULT_VOICE_LANGUAGE = 'en';

export const RTL_VOICE_LANGUAGES = new Set(['ar', 'ur'] as const);

/** BCP-47 locales for Web Speech recognition and browser TTS. */
export const SPEECH_RECOGNITION_LOCALES: Record<string, string> = {
  en: 'en-US',
  ar: 'ar-SA',
  hi: 'hi-IN',
  ur: 'ur-PK',
  bn: 'bn-BD',
  fr: 'fr-FR',
  de: 'de-DE',
  zh: 'zh-CN',
  ja: 'ja-JP',
};

export type SupportedVoiceLanguage = (typeof SUPPORTED_VOICE_LANGUAGES)[number];

export interface ResolveVoiceLanguageInput {
  /** Highest precedence — explicit override (e.g. per-utterance). */
  userLanguage?: string | null;
  /** From voice settings (`default_language`). */
  settingsLanguage?: string | null;
  /** App UI language (i18n). */
  i18nLanguage?: string | null;
  /** Browser / navigator language. */
  navigatorLanguage?: string | null;
}

function normalizeLanguageCode(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const primary = raw.trim().toLowerCase().split(/[-_]/)[0];
  if (!primary) return null;
  return (SUPPORTED_VOICE_LANGUAGES as readonly string[]).includes(primary)
    ? primary
    : null;
}

function readCachedSettingsLanguage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('voiceSettings');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { default_language?: string };
    return normalizeLanguageCode(parsed?.default_language);
  } catch {
    return null;
  }
}

function readI18nLanguage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return normalizeLanguageCode(localStorage.getItem('i18nextLng'));
}

function readNavigatorLanguage(override?: string | null): string | null {
  if (override !== undefined) return normalizeLanguageCode(override);
  if (typeof navigator === 'undefined') return null;
  return normalizeLanguageCode(navigator.language);
}

/**
 * Resolve app / speech / transcription / TTS language and text direction.
 * Precedence: explicit → settings → i18n → navigator → default (`en`).
 */
export function resolveVoiceLanguage(
  input: ResolveVoiceLanguageInput = {}
): ResolvedVoiceLanguage {
  const appLanguage =
    normalizeLanguageCode(input.userLanguage) ||
    normalizeLanguageCode(input.settingsLanguage) ||
    readCachedSettingsLanguage() ||
    normalizeLanguageCode(input.i18nLanguage) ||
    readI18nLanguage() ||
    readNavigatorLanguage(input.navigatorLanguage) ||
    DEFAULT_VOICE_LANGUAGE;

  const speechRecognitionLocale =
    SPEECH_RECOGNITION_LOCALES[appLanguage] ||
    SPEECH_RECOGNITION_LOCALES[DEFAULT_VOICE_LANGUAGE];

  return {
    appLanguage,
    speechRecognitionLocale,
    transcriptionLanguage: appLanguage,
    ttsLanguage: appLanguage,
    direction: RTL_VOICE_LANGUAGES.has(appLanguage as 'ar' | 'ur') ? 'rtl' : 'ltr',
  };
}

/** Convenience: BCP-47 speech locale only. */
export function resolveSpeechRecognitionLocale(
  input?: ResolveVoiceLanguageInput
): string {
  return resolveVoiceLanguage(input).speechRecognitionLocale;
}

/** Convenience: 2-letter transcription / Whisper language. */
export function resolveTranscriptionLanguage(
  input?: ResolveVoiceLanguageInput
): string {
  return resolveVoiceLanguage(input).transcriptionLanguage;
}
